import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { IBusiness, IContactInfo } from '@trii/types/dist/Contacts';
import { useEffect, useRef, useState } from 'react';
import type { UIEvent } from 'react';

import { useEditContactApi } from '../../context/EditContactApiContext';
import { useTranslationContext } from '../../context/TranslationContext';
import type { Pagination } from '../../services/api';

type Props = {
  business: IBusiness | null;
  onBack: () => void;
  onEntityUpdated?: (entity: unknown) => void;
};

const DEFAULT_PAGINATION: Omit<Pagination, 'filter' | 'currentPage'> = {
  format: 'IContactInfo',
  total: 0,
  lastPage: 1,
  perPage: 10,
  order: 'DESC',
  orderColumn: 'name',
};

const getMemberName = (member: IContactInfo) =>
  String((member as any).name || (member as any).profileName || (member as any).address || '');

const BusinessMembersEditor = ({ business, onBack, onEntityUpdated }: Props) => {
  const t = useTranslationContext();
  const api = useEditContactApi();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [selectedMembers, setSelectedMembers] = useState<IContactInfo[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<IContactInfo[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTimer, setIsLoadingTimer] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [allLoaded, setAllLoaded] = useState(false);

  useEffect(() => {
    setSelectedMembers(((business as any)?.members ?? []) as IContactInfo[]);
  }, [business]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      abortRef.current?.abort();
    };
  }, []);

  const resetSearchState = () => {
    abortRef.current?.abort();
    abortRef.current = null;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setHasSearched(false);
    setAllLoaded(false);
    setCurrentPage(1);
    setOptions([]);
  };

  const fetchPage = async ({ query, page }: { query: string; page: number }) => {
    if (!api) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    try {
      const pagination: Pagination = {
        ...DEFAULT_PAGINATION,
        currentPage: page,
        filter: [
          {
            column: 'Name',
            condition: '',
            value: query,
          },
        ],
      };

      const response = await api.fetchBusinessTableData({
        pagination,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;

      const next = ((response as any)?.contactos ?? []) as IContactInfo[];
      const total = (response as any)?.paginacion?.total ?? 0;

      setHasSearched(true);
      setOptions((prev) => {
        const merged =
          page === 1
            ? next
            : (() => {
                const byId = new Map<string, IContactInfo>();
                for (const item of prev) byId.set(item.id, item);
                for (const item of next) byId.set(item.id, item);
                return Array.from(byId.values());
              })();

        if (total && merged.length >= total) {
          setAllLoaded(true);
        }

        return merged;
      });
    } finally {
      setIsLoading(false);
      setIsLoadingTimer(false);
    }
  };

  const handleQueryChanged = (value: string) => {
    resetSearchState();
    if (!value) return;

    setIsLoadingTimer(true);
    timerRef.current = setTimeout(() => {
      void fetchPage({ query: value, page: 1 });
    }, 800);
  };

  const handleScroll = (event: UIEvent<HTMLElement>) => {
    const element = event.target as HTMLElement;
    const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 1;

    if (!bottom || allLoaded || isLoading || !inputValue) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    void fetchPage({ query: inputValue, page: nextPage });
  };

  const handleSave = async () => {
    if (!api || !business?.id || isSaving) return;

    setIsSaving(true);
    try {
      const updated = await api.updateEntity({
        entityType: 'business',
        entityId: business.id,
        body: { members: selectedMembers } as any,
      });
      onEntityUpdated?.(updated);
      onBack();
    } finally {
      setIsSaving(false);
    }
  };

  const noOptionsNode =
    hasSearched && inputValue ? (
      <Typography variant="body2">{t('editContactModal.businessMembers.noOptions')}</Typography>
    ) : (
      <Typography variant="body2">{t('editContactModal.businessMembers.searchPrompt')}</Typography>
    );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton size="small" onClick={onBack} aria-label={t('global.back')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1">{t('editContactModal.businessMembers.edit')}</Typography>

        <Box sx={{ flex: 1 }} />

        <LoadingButton
          variant="contained"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!api || !business?.id}
        >
          {t('global.save')}
        </LoadingButton>
      </Stack>

      <Box sx={{ position: 'relative' }}>
        {isLoading || isLoadingTimer ? (
          <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0 }}>
            <LinearProgress sx={{ borderRadius: 1 }} />
          </Box>
        ) : null}

        <Autocomplete
          multiple
          fullWidth
          disablePortal
          disableCloseOnSelect
          value={selectedMembers}
          onChange={(event, newValue) => {
            void event;
            setSelectedMembers(newValue);
          }}
          inputValue={inputValue}
          onInputChange={(event, newValue, reason) => {
            void event;
            setInputValue(newValue);
            if (reason !== 'input') return;
            handleQueryChanged(newValue);
          }}
          options={options}
          filterOptions={(x) => x}
          getOptionLabel={getMemberName}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={noOptionsNode}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                label={getMemberName(option)}
                size="small"
                {...getTagProps({ index })}
                key={option.id}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('editContactModal.businessMembers.searchLabel')}
              placeholder={t('editContactModal.businessMembers.searchPlaceholder')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading || isLoadingTimer ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          ListboxProps={{
            sx: {
              maxHeight: 240,
              overflowY: 'auto',
            },
            onScroll: handleScroll,
          }}
        />
      </Box>
    </Box>
  );
};

export default BusinessMembersEditor;
